import React from 'react'
import { observer } from 'mobx-react'
import JobCreationViewModel from 'jobs/creation/JobCreationViewModel'
import LegendSelectorViewModel from 'jobs/creation/LegendSelectorViewModel'
import { QuestionStepViewModel } from 'jobs/creation/IntakeQuestionsViewModel'
import CompactLegendSelector from 'jobs/creation/views/JobCreation/JobCreationFormView/CompactLegendSelector'
import {
  renderStepContent,
  StepView,
} from 'jobs/creation/views/JobCreationDialog/JobCreationQuestions'
import { ModalType } from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'
import { LegendServiceType } from 'legends/PublishedLegend'
import styles from './JobSection.sass'

interface Props {
  jobCreation: JobCreationViewModel
  requiredQuestionsAmount: number
}

const MODAL_TYPE: ModalType = 'NewDraft'

export default observer(function CallCenterJobCreationForm({
  jobCreation,
  requiredQuestionsAmount,
}: Props) {
  const legendSteps = jobCreation.legendSelector.stepContainers
  const questionSteps = jobCreation.intakeQuestions.stepContainers

  const requiredCount = React.useMemo(() => {
    if (requiredQuestionsAmount <= 0) {
      return 0
    }
    return Math.min(requiredQuestionsAmount, questionSteps.length)
  }, [requiredQuestionsAmount, questionSteps.length])

  const requiredQuestionSteps = React.useMemo(
    () => questionSteps.slice(0, requiredCount),
    [questionSteps, requiredCount],
  )

  const optionalQuestionSteps = React.useMemo(
    () => questionSteps.slice(requiredCount),
    [questionSteps, requiredCount],
  )

  const taxTypeLegends = jobCreation.legendSelector.legends.filter(
    (legend) => legend.serviceType === LegendServiceType.Tax,
  )
  const jobWasCreated = jobCreation.jobWasCreated

  return (
    <div className={styles.formContainer}>
      {legendSteps.map((stepContainer) => {
        if (stepContainer instanceof LegendSelectorViewModel) {
          const legendContent = taxTypeLegends.length ? (
            <CompactLegendSelector
              vm={stepContainer}
              disabled={jobWasCreated}
              searchResults={taxTypeLegends}
            />
          ) : (
            <div className={styles.placeholder}>
              No tax legends are available for this workspace.
            </div>
          )

          return (
            <StepView
              key={stepContainer.step.stepId}
              stepContainer={stepContainer}
              modalType={MODAL_TYPE}
            >
              <div className={styles.questionWrapper}>
                <span className={styles.legendNotice}>
                  Only tax legends are available for call center submissions.
                </span>
                {legendContent}
              </div>
            </StepView>
          )
        }

        return null
      })}

      {requiredQuestionSteps.map((stepContainer) =>
        renderQuestionStep(stepContainer, true, jobWasCreated),
      )}

      {optionalQuestionSteps.map((stepContainer) =>
        renderQuestionStep(stepContainer, false, jobWasCreated),
      )}

      {questionSteps.length === 0 && legendSteps.length === 0 && (
        <div className={styles.placeholder}>No questions available.</div>
      )}
    </div>
  )
})

function renderQuestionStep(
  stepContainer: QuestionStepViewModel,
  isRequired: boolean,
  jobWasCreated: boolean,
) {
  const stepContent = renderStepContent(
    stepContainer,
    jobWasCreated,
    MODAL_TYPE,
  )

  if (!stepContent) {
    return null
  }

  const badgeClass = isRequired
    ? `${styles.badge} ${styles.requiredBadge}`
    : `${styles.badge} ${styles.optionalBadge}`
  const badgeLabel = isRequired ? 'Required' : 'Optional'

  return (
    <StepView
      key={stepContainer.step.stepId}
      stepContainer={stepContainer}
      modalType={MODAL_TYPE}
    >
      <div className={styles.questionWrapper}>
        <span className={badgeClass}>{badgeLabel}</span>
        {stepContent}
      </div>
    </StepView>
  )
}
